import React, { useEffect, useState } from 'react';
import { Calculator, ShieldCheck, ShieldAlert, FileClock, RefreshCw, Pencil, Save, X } from 'lucide-react';
import {
  getTaxSummary, getTaxRules, getTaxProfiles, getTaxCatalog,
  saveTaxProfile, verifyTaxProfile,
} from '../utils/api';

const pct = (v) => `${Number(v || 0).toLocaleString('pt-PT', { maximumFractionDigits: 4 })}%`;
const kz = (v) => `${Number(v || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kz`;
const ESTADO = {
  VERIFIED: 'Verificado', UNVERIFIED: 'Por verificar', REJECTED: 'Rejeitado',
  SUBJECT: 'Sujeito', NOT_SUBJECT: 'Não sujeito', CONDITIONAL: 'Condicional', UNKNOWN: 'Por apurar',
};

const Select = ({ value, onChange, children }) => (
  <select value={value || 'UNKNOWN'} onChange={onChange}
    className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100">
    {children}
  </select>
);

const Card = ({ icon: Icon, label, value, note, alert }) => (
  <div className={`rounded-xl border p-4 ${alert ? 'border-amber-500/40 bg-amber-500/10' : 'border-gray-700 bg-gray-800'}`}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400"><Icon size={15} />{label}</div>
    <div className={`mt-2 text-2xl font-bold ${alert ? 'text-amber-300' : 'text-white'}`}>{value}</div>
    <p className="mt-1 text-xs text-gray-500">{note}</p>
  </div>
);

export default function TaxEnginePage() {
  const [summary, setSummary] = useState(null);
  const [rules, setRules] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true); setError('');
    Promise.all([getTaxSummary(), getTaxRules(), getTaxProfiles(), getTaxCatalog()])
      .then(([s, r, p, c]) => { setSummary(s); setRules(r); setProfiles(p); setCatalog(c); })
      .catch(e => setError(e?.response?.data?.message || 'Não foi possível carregar o motor fiscal.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const editar = (profile) => {
    setEditing(profile.tax_profile_id);
    setDraft({
      ...profile,
      vat_rate: profile.vat_rate ?? '',
      withholding_rate: profile.withholding_rate ?? '',
    });
    setNotice('');
  };

  const guardar = async () => {
    setBusy(true); setError('');
    try {
      await saveTaxProfile(draft.owner_type, draft.owner_id, {
        country: draft.country, nif: draft.nif, taxpayer_type: draft.taxpayer_type,
        tax_regime: draft.tax_regime, vat_regime: draft.vat_regime,
        vat_rate: draft.vat_rate === '' ? null : Number(draft.vat_rate),
        withholding_status: draft.withholding_status,
        withholding_type: draft.withholding_type,
        withholding_rate: draft.withholding_rate === '' ? null : Number(draft.withholding_rate),
        withholding_agent_status: draft.withholding_agent_status,
        accounting_regime: draft.accounting_regime,
        irt_group: draft.irt_group, industrial_tax_regime: draft.industrial_tax_regime,
        vat_withholding_status: draft.vat_withholding_status,
        vat_withholding_rate: draft.vat_withholding_rate,
        residency_status: draft.residency_status,
        source_document: draft.source_document,
      });
      setEditing(null); setDraft(null);
      setNotice('Nova versão guardada. Deve ser validada por outra pessoa autorizada.');
      load();
    } catch (e) { setError(e?.response?.data?.message || 'Não foi possível guardar o perfil fiscal.'); }
    finally { setBusy(false); }
  };

  const validar = async (profile, approve) => {
    const reason = approve ? null : window.prompt('Indique o motivo da rejeição:');
    if (!approve && !reason) return;
    setBusy(true); setError('');
    try {
      await verifyTaxProfile(profile.tax_profile_id, approve, reason);
      setNotice(approve ? 'Perfil fiscal verificado.' : 'Perfil fiscal rejeitado.');
      load();
    } catch (e) { setError(e?.response?.data?.message || 'Não foi possível validar o perfil fiscal.'); }
    finally { setBusy(false); }
  };

  return <div className="space-y-6">
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1 className="text-2xl font-bold text-white">Motor fiscal</h1>
        <p className="text-sm text-gray-400 mt-1">Perfis, regras versionadas e cálculos fiscais auditáveis.</p>
      </div>
      <button onClick={load} className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700">
        <RefreshCw size={15} /> Atualizar
      </button>
    </div>

    {error && <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}
    {notice && <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200">{notice}</div>}
    {loading && <div className="text-sm text-gray-400">A carregar os dados fiscais…</div>}
    {summary && <>
      {summary.dozy_profile?.verification_status !== 'VERIFIED' && <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
        <div className="flex gap-3"><ShieldAlert className="text-amber-300 shrink-0" />
          <div><strong className="text-amber-200">Perfil fiscal da Dozy por validar</strong>
            <p className="text-sm text-amber-100/70 mt-1">Complete NIF, séries de faturação e enquadramento; depois submeta a aprovação separada. Os cálculos mostram este aviso até à validação.</p>
          </div>
        </div>
      </div>}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card icon={ShieldCheck} label="Perfis verificados" value={summary.profiles.VERIFIED || 0} note="Versões vigentes aprovadas" />
        <Card icon={ShieldAlert} label="Por verificar" value={summary.profiles.UNVERIFIED || 0} note="Não podem ser assumidos como conformes" alert={(summary.profiles.UNVERIFIED || 0) > 0} />
        <Card icon={Calculator} label="Cálculos guardados" value={summary.calculations.total || 0} note={`IVA ${kz(summary.calculations.vat)}`} />
        <Card icon={FileClock} label="Documentos pendentes" value={summary.pending_documents || 0} note="Inclui comprovativos de retenção" alert={(summary.pending_documents || 0) > 0} />
      </div>
    </>}

    <section className="rounded-xl border border-gray-700 bg-gray-800 overflow-hidden">
      <header className="px-4 py-3 border-b border-gray-700">
        <h2 className="font-semibold text-white">Perfis fiscais vigentes</h2>
        <p className="text-xs text-gray-500 mt-1">A sujeição do prestador e a obrigação do pagador são decisões diferentes. Uma alteração cria nova versão e exige validação separada.</p>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] text-sm">
          <thead className="text-xs uppercase text-gray-500"><tr>
            <th className="text-left p-3">Titular</th><th className="text-left p-3">Contribuinte</th>
            <th className="text-left p-3">IVA</th><th className="text-left p-3">Rendimento sujeito</th>
            <th className="text-left p-3">Agente pagador</th><th className="text-left p-3">Estado</th>
            <th className="text-right p-3">Ações</th>
          </tr></thead>
          <tbody>{profiles.map((p) => <React.Fragment key={p.tax_profile_id}>
            <tr className="border-t border-gray-700/70">
              <td className="p-3"><strong className="text-gray-100">{p.owner_name}</strong><span className="block text-xs text-gray-500">{p.owner_type}{p.owner_id ? ` #${p.owner_id}` : ''} · NIF {p.nif || 'por indicar'}</span></td>
              <td className="p-3 text-gray-300">{p.taxpayer_type}</td>
              <td className="p-3 text-gray-300">{p.vat_regime}{p.vat_rate != null ? ` · ${pct(p.vat_rate)}` : ''}</td>
              <td className="p-3 text-gray-300">{ESTADO[p.withholding_status] || p.withholding_status}</td>
              <td className="p-3 text-gray-300">{ESTADO[p.withholding_agent_status] || p.withholding_agent_status || 'Por apurar'}</td>
              <td className="p-3"><span className={`rounded-full px-2 py-1 text-xs ${p.verification_status === 'VERIFIED' ? 'bg-emerald-500/15 text-emerald-300' : p.verification_status === 'REJECTED' ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300'}`}>{ESTADO[p.verification_status] || p.verification_status}</span></td>
              <td className="p-3"><div className="flex justify-end gap-2">
                <button disabled={busy} onClick={() => editar(p)} className="rounded-lg border border-gray-600 p-2 text-gray-300 hover:bg-gray-700" title="Editar perfil"><Pencil size={14}/></button>
                {p.verification_status === 'UNVERIFIED' && <>
                  <button disabled={busy} onClick={() => validar(p, true)} className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-500">Validar</button>
                  <button disabled={busy} onClick={() => validar(p, false)} className="rounded-lg border border-red-500/40 px-3 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/10">Rejeitar</button>
                </>}
              </div></td>
            </tr>
            {editing === p.tax_profile_id && draft && <tr className="border-t border-blue-500/30 bg-gray-900/60"><td colSpan="7" className="p-4">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <label className="text-xs text-gray-400">Tipo de contribuinte<Select value={draft.taxpayer_type} onChange={(e) => setDraft(v => ({...v, taxpayer_type: e.target.value}))}>{(catalog?.taxpayer_types || []).map(v => <option key={v}>{v}</option>)}</Select></label>
                <label className="text-xs text-gray-400">Regime de IVA<Select value={draft.vat_regime} onChange={(e) => setDraft(v => ({...v, vat_regime: e.target.value}))}>{(catalog?.vat_regimes || []).map(v => <option key={v}>{v}</option>)}</Select></label>
                <label className="text-xs text-gray-400">Rendimento sujeito a retenção<Select value={draft.withholding_status} onChange={(e) => setDraft(v => ({...v, withholding_status: e.target.value}))}>{(catalog?.withholding_states || []).map(v => <option key={v} value={v}>{ESTADO[v] || v}</option>)}</Select></label>
                <label className="text-xs text-gray-400">Pagador é agente de retenção<Select value={draft.withholding_agent_status} onChange={(e) => setDraft(v => ({...v, withholding_agent_status: e.target.value}))}>{(catalog?.withholding_agent_states || []).map(v => <option key={v} value={v}>{ESTADO[v] || v}</option>)}</Select></label>
                <label className="text-xs text-gray-400">Regime contabilístico<Select value={draft.accounting_regime} onChange={(e) => setDraft(v => ({...v, accounting_regime: e.target.value}))}>{(catalog?.accounting_regimes || []).map(v => <option key={v}>{v}</option>)}</Select></label>
                <label className="text-xs text-gray-400">Tipo de retenção<Select value={draft.withholding_type || 'NONE'} onChange={(e) => setDraft(v => ({...v, withholding_type: e.target.value}))}><option>NONE</option><option>IRT</option><option>INDUSTRIAL_TAX</option><option>OTHER</option></Select></label>
                <label className="text-xs text-gray-400">Taxa de IVA<input type="number" min="0" max="100" step="0.0001" value={draft.vat_rate} onChange={(e) => setDraft(v => ({...v, vat_rate: e.target.value}))} className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100"/></label>
                <label className="text-xs text-gray-400">Taxa de retenção<input type="number" min="0" max="100" step="0.0001" value={draft.withholding_rate} onChange={(e) => setDraft(v => ({...v, withholding_rate: e.target.value}))} className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100"/></label>
              </div>
              <p className="mt-3 text-xs text-amber-300/80">Guardar não valida. Outra pessoa autorizada terá de confirmar esta nova versão.</p>
              <div className="mt-3 flex justify-end gap-2"><button onClick={() => { setEditing(null); setDraft(null); }} className="flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-sm text-gray-300"><X size={14}/>Cancelar</button><button disabled={busy} onClick={guardar} className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white"><Save size={14}/>{busy ? 'A guardar…' : 'Guardar versão'}</button></div>
            </td></tr>}
          </React.Fragment>)}</tbody>
        </table>
      </div>
    </section>

    <section className="rounded-xl border border-gray-700 bg-gray-800 overflow-hidden">
      <header className="px-4 py-3 border-b border-gray-700">
        <h2 className="font-semibold text-white">Regras fiscais versionadas</h2>
        <p className="text-xs text-gray-500 mt-1">As taxas abaixo são configuração; alterações criam uma nova versão e não mudam faturas antigas.</p>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="text-xs uppercase text-gray-500"><tr>
            <th className="text-left p-3">Código</th><th className="text-left p-3">Operação</th>
            <th className="text-left p-3">Tipo</th><th className="text-right p-3">Taxa</th>
            <th className="text-center p-3">Versão</th><th className="text-left p-3">Estado</th>
          </tr></thead>
          <tbody>{rules.map(r => <tr key={r.tax_rule_id} className="border-t border-gray-700/70">
            <td className="p-3"><strong className="text-gray-200">{r.name}</strong><span className="block text-xs text-gray-500">{r.tax_code}</span></td>
            <td className="p-3 text-gray-300">{r.operation_type || 'Todas'}</td>
            <td className="p-3 text-gray-300">{r.tax_type}</td>
            <td className="p-3 text-right font-semibold text-white">{pct(r.rate)}</td>
            <td className="p-3 text-center text-gray-300">v{r.version}</td>
            <td className="p-3"><span className={`rounded-full px-2 py-1 text-xs ${r.status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-gray-700 text-gray-300'}`}>{r.status}</span></td>
          </tr>)}</tbody>
        </table>
      </div>
    </section>
  </div>;
}
